import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import { DateTimePickerStyle } from "@waytek/MobiusOverrides/DatePicker/DatePicker";
import { colorStrings, fontFamily, fontFamilyAlt, mediaQueriesBP } from "@waytek/Utilities/variables";
import { cartLineScheduleShipmentModalStyles as styles } from "@waytek/Widgets/Cart/CartLinesScheduleShipmentModal";
import { css } from "styled-components";

const localColors = {
    globalIndustrialTaupe: getColor("common.global.industrialTaupe"),
    globalDarkerGray: getColor("common.global.darkerGray"),
    globalWhite: getColor("common.global.white"),
    globalSecondaryGray: getColor("common.global.secondaryGray"),
    globalGuardianBlue: getColor("common.global.guardianBlue"),
};

styles.productDescription = {
    ...styles.productDescription,
    productDetailLink: {
        css: css`
            font-size: 16px;
            font-weight: bold;
            font-family: ${fontFamily};
            font-stretch: normal;
            font-style: normal;
            line-height: 1.5;
            letter-spacing: normal;
            &&& {
                span {
                    font-size: 16px;
                    border: 0;
                    text-decoration: none;

                    &:hover {
                        span {
                            border: 0;
                            text-decoration: underline;
                        }
                    }
                }
            }

            ${mediaQueriesBP.maxMD} {
                width: 80%;
            }
        `,
    },
};

styles.productPartNumbers = {
    ...styles.productPartNumbers,
    erpNumberLabelText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
            margin-right: 5px;
            white-space: nowrap;
        `,
    },
    erpNumberValueText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
        `,
    },

    manufacturerItemLabelText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
            margin-right: 5px;
            white-space: nowrap;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
        `,
    },
};

styles.subtotalWrapper = {
    ...styles.subtotalWrapper,
    css: css`
        justify-content: flex-end;
        display: flex;
        width: 100%;
        margin-top: 5px;

        p,
        span {
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
            text-align: right;
            font-family: ${fontFamilyAlt};
            color: ${localColors.globalSecondaryGray};
        }

        ${mediaQueriesBP.maxMD} {
            margin: 0;
            text-align: left;
            justify-content: flex-start;
        }
    `,
};

styles.modalWrapper = {
    ...styles.modalWrapper,
    cssOverrides: {
        ...styles.modalWrapper?.cssOverrides,
        modalContent: css`
            overflow-x: hidden;
        `,
    },
};

styles.subtotalLabel = {
    ...styles.subtotalLabel,
    css: css`
        ${mediaQueriesBP.maxMD} {
            display: none;
        }

        ${mediaQueriesBP.minMD} {
            margin-right: 6px;
        }
    `,
};

styles.productMinQtyStockItem = {
    ...styles.productMinQtyStockItem,
    css: css`
        margin: 0;
        width: 100%;
        text-align: right;

        ${mediaQueriesBP.maxMD} {
            order: 2;
            text-align: left;
        }
    `,
};

styles.productMinQtyStockLabel = {
    ...styles.productMinQtyStockLabel,
    weight: 600,
    lineHeight: 1.71,
    css: css`
        flex: 0 0 auto;
        font-size: 14px;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        padding-right: 0.4em;
    `,
};

styles.productMinQtyStockValue = {
    ...styles.productMinQtyStockValue,
    weight: "normal",
    lineHeight: 1.71,
    css: css`
        flex: 1 1 auto;
        min-width: 0;
        font-size: 14px;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
    `,
};

styles.productMultipleStockItem = {
    ...styles.productMultipleStockItem,
    css: css`
        margin: 0;
        width: 100%;
        text-align: right;

        ${mediaQueriesBP.maxMD} {
            order: 2;
            text-align: left;
        }
    `,
};

styles.productMultipleStockLabel = {
    ...styles.productMultipleStockLabel,
    weight: 600,
    lineHeight: 1.71,
    css: css`
        flex: 0 0 auto;
        font-size: 14px;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        padding-right: 0.4em;
    `,
};

styles.productMultipleStockValue = {
    ...styles.productMultipleStockValue,
    weight: "normal",
    lineHeight: 1.71,
    css: css`
        flex: 1 1 auto;
        min-width: 0;
        font-size: 14px;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
    `,
};

styles.productStockWrapper = {
    ...styles.productStockWrapper,
    css: css`
        width: 100%;
        text-align: right;

        ${mediaQueriesBP.maxMD} {
            display: none;
        }
    `,
};

styles.productExcedeeWrapper = {
    ...styles.productExcedeeWrapper,
};

styles.productMultipleGlobalWrapper = {
    ...styles.productMultipleGlobalWrapper,
    css: css`
        ${mediaQueriesBP.maxMD} {
            order: 6;
        }
    `,
};

styles.productNumbersMobile = {
    ...styles.productNumbersMobile,
    css: css`
        padding-top: 10px;
        order: 1;
        div {
            padding-top: 0;
            padding-bottom: 0;
        }
        ${mediaQueriesBP.minMD} {
            display: none;
        }
    `,
};

styles.modalTopSection = {
    ...styles.modalTopSection,
    css: css`
        padding: 25px 15px;
    `,
};

styles.modalImageGridItem = {
    ...styles.modalImageGridItem,
    width: [4, 4, 2, 2, 2],
    css: css`
        &&& {
            padding: 10px;
            border: 1px solid ${localColors.globalIndustrialTaupe};
            display: flex;
            justify-content: center;
            align-items: center;
            max-width: 120px;
            max-height: 90px;
            img {
                aspect-ratio: 4/3;
                object-fit: contain;
            }

            ${mediaQueriesBP.maxMD} {
                max-width: 100px;
                max-height: 75px;
            }
        }
    `,
};

styles.modalProductDescriptionGridItem = {
    ...styles.modalProductDescriptionGridItem,
    width: [8, 8, 6, 6, 6],
    css: css`
        flex-direction: column;
        padding-top: 0;

        > div {
            text-align: left;
        }

        .modalDescriptionItem {
            margin-bottom: 5px;
            display: block;

            ${mediaQueriesBP.maxMD} {
                display: none;

                &:nth-child(1) {
                    display: block;
                }
            }
        }
    `,
};

styles.modalQuantityGridItem = {
    ...styles.modalQuantityGridItem,
    width: [12, 12, 4, 4, 4],
    css: css`
        flex-direction: column;
        align-items: flex-end;
        padding-top: 0;
        padding-right: 0;

        .mobileInfo {
            display: none;
        }

        ${mediaQueriesBP.maxMD} {
            align-items: flex-start;
            padding-left: 0;

            .mobileInfo {
                display: block;
                text-align: left;
            }

            .productNumber {
                order: 1;
            }

            .productMinQtyStockItem {
                order: 2;
                margin-bottom: 5px;
            }

            .stockinfo {
                order: 3;
            }

            .priceInfo {
                order: 4;
            }
        }
    `,
};

styles.modalPriceWrapper = {
    ...styles.modalPriceWrapper,
    css: css`
        flex-direction: column;
        ${mediaQueriesBP.minMD} {
            padding-left: 0;
            padding-right: 0;
        }

        &&& {
            section {
                display: flex;
                flex-direction: row;
                align-items: center;
                font-size: 14px;
                font-family: ${fontFamilyAlt};
                color: ${localColors.globalSecondaryGray};
                ${mediaQueriesBP.minMD} {
                    justify-content: flex-end;
                }

                ${mediaQueriesBP.maxMD} {
                    order: 4;
                    margin: 5px 0;
                }

                legend {
                    margin-right: 5px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: ${fontFamilyAlt};
                    padding: 0;
                    color: ${localColors.globalSecondaryGray};
                }

                span {
                    font-size: 14px;
                    font-weight: normal;
                    font-family: ${fontFamilyAlt};
                    color: ${localColors.globalSecondaryGray};
                }
            }
        }
    `,
};

styles.modalQuantityWrapper = {
    ...styles.modalQuantityWrapper,
    css: css`
        display: flex;
        width: 100%;
        margin-top: 5px;
        ${mediaQueriesBP.minMD} {
            justify-content: flex-end;
            flex-direction: column;
            text-align: right;
        }

        ${mediaQueriesBP.maxMD} {
            order: 5;
            margin-top: 0;
        }

        > div {
            flex-direction: row;
            align-items: center;
            ${mediaQueriesBP.minMD} {
                justify-content: flex-end;
            }

            ${mediaQueriesBP.maxMD} {
                width: 99px;
                margin-right: 10px;
            }

            div {
                &&& {
                    margin-top: 0;
                }
            }
        }

        &&& {
            label {
                font-size: 14px;
                margin-right: 5px;
                font-weight: 600;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.71;
                letter-spacing: normal;
                text-align: right;
                text-transform: unset;
                ${mediaQueriesBP.maxMD} {
                    display: none;
                }

                + div {
                    margin-top: 0;
                    width: auto;
                }
            }

            input {
                height: 40px;
                width: 90px;

                ${mediaQueriesBP.maxMD} {
                    width: 99px;
                }
            }
        }
    `,
};

styles.modalSectionTitle = {
    ...styles.modalSectionTitle,
    css: css`
        font-size: 18px;
        font-family: ${fontFamily};
        color: ${localColors.globalSecondaryGray};
        border-bottom: 3px solid ${localColors.globalIndustrialTaupe};
        font-weight: bold;
        padding-bottom: 4px;
        width: 100%;
    `,
};

styles.modalBottomText = {
    ...styles.modalBottomText,
    width: [12, 12, 8, 8, 8],
    css: css`
        span {
            font-size: 12px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            font-family: ${fontFamilyAlt};
            color: ${localColors.globalSecondaryGray};
        }
    `,
};

styles.modalBottomControls = {
    ...styles.modalBottomControls,
    width: [12, 12, 4, 4, 4],
    css: css`
        justify-content: flex-end;

        ${mediaQueriesBP.maxMD} {
            padding-top: 5px;
        }
    `,
};

styles.modalBackButton = {
    ...styles.modalBackButton,
    variant: "secondary",
    css: css`
        margin-right: 12px;
    `,
};

styles.editableQuantityTextField = {
    labelProps: {
        variant: "legend",
        css: css`
            font-size: 10px;
            text-transform: uppercase;
            font-weight: 600;
        `,
    },
};

styles.shipmentDatePicker = {
    ...styles.shipmentDatePicker,
    clearIconProps: {
        css: css`
            display: none;
        `,
    },
    cssOverrides: {
        ...styles.shipmentDatePicker?.cssOverrides,
        formInputWrapper: css`
            ${styles.shipmentDatePicker?.cssOverrides?.formInputWrapper}
            min-width: 180px;

            &&&& {
                ${IconWrapper} {
                    justify-content: center;
                    padding: 0;
                }

                ${DateTimePickerStyle} {
                    .react-calendar {
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        min-height: 275px;
                        width: 300px;
                    }
                }
            }
        `,
    },
    errorProps: {
        css: css`
            display: none;
        `,
    },
};

styles.addRemoveButton = {
    ...styles.addRemoveButton,
    css: css`
        padding: 0;
        background: none;
        border: 0;
        margin-left: 10px;

        &::after {
            content: none;
        }

        &:disabled {
            svg {
                color: ${getColor(colorStrings.commonGlobalMediumLightGray)};
            }
        }
    `,
};

styles.addRemoveButtonIcon = {
    ...styles.addRemoveButtonIcon,
    mergeCss: true,
    css: css`
        &&&& {
            color: ${getColor(colorStrings.commonGlobalGuardianBlue)};

            &:disabled {
                color: ${getColor(colorStrings.commonGlobalMediumLightGray)};
            }
        }
    `,
};

styles.errorMessageGrid = {
    ...styles.errorMessageGrid,
    width: 12,
    css: css`
        &&& {
            margin-top: -12px;
        }
    `,
};

styles.errorMessage = {
    ...styles.errorMessage,
    color: "#ff0000",
    size: "12px",
    lineHeight: "17px",
};
